import * as React from 'react';
import { Box, Stack, TablePagination, Tooltip } from '@mui/material';
import Filter from 'component/filter';
import colors from 'assets/theme/base/colors';
import borders from 'assets/theme/base/borders';
import FlexibleTable from 'component/flexible-table';
import { useEffect, useState } from 'react';
import { RowDataType, TableMetaData } from 'types/common/common';
import { useAppDispatch } from 'hooks/redux';
import { ReactComponent as Delete } from 'assets/images/staff-user/delete.svg';
import { ReactComponent as Condition } from 'assets/images/staff-user/edit.svg';
import { openConfirmationDialogs } from 'store/slices/confirmation-dialog/confirmation-dialog-slice';
import pxToRem from 'assets/theme/function/px-to-rem';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { RuleData, RuleListRequest } from 'types/rule/rule';
import { useLazyRuleListQuery } from 'store/slices/rule/add-rule';
import { useRuleDeleteMutation } from 'store/slices/rule/delete-rule';
import { useNavigate } from 'react-router-dom';
import { StyledTableRow } from 'component/tabel-pagination-style';
import { openBackdrop } from 'store/slices/backdrop/backdrop-slice';

export const initialListData: RuleListRequest = {
    page: 1,
    pageSize: 10,
};

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'name',
        position: 1,
        title: 'Name',
        isSortable: false,
    },
    {
        key: 'description',
        position: 2,
        title: 'Description',
        isSortable: false,
    },
    {
        key: 'entities',
        position: 3,
        title: 'Entities',
        isSortable: false,
    },
    {
        key: 'createdDate',
        position: 7,
        title: 'Created DataCondition',
        isSortable: false,
        type: 'date',
        format: 'DD.MM.YYYY HH:mm',
    },
    {
        key: 'action',
        position: 8,
        title: 'Action',
        isSortable: false,
    },
];

const Rule: React.FC = () => {
    const [tableMetaDataList, setTableMetaDataList] = useState<TableMetaData[]>(
        defaultTableStaffDataList,
    );
    const [dataRuleRequestList] =
        React.useState<RuleListRequest>(initialListData);
    const [pagesCount, setPagesCount] = useState<number>(0);
    const [page, setPage] = useState<number>(initialListData?.page);
    const [pageSize, setPageSize] = useState<number>(initialListData?.pageSize);
    const [sort, setSort] = useState<string>('');
    const [tableData, setTableData] = useState<RowDataType[]>([]);

    const [getRuleList, { data, isFetching }] = useLazyRuleListQuery();

    const [
        ruleDeleteApi,
        {
            error: deleteError,
            isError: deleteIsError,
            isSuccess: deleteIsSuccess,
        },
    ] = useRuleDeleteMutation();

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const createNewRule = () => {
        navigate(`/product/rule-detail`);
    };

    const deleteRule = ({ ruleId }: RuleData) => {
        if (ruleId) {
            ruleDeleteApi(ruleId);
        }
    };
    const handleFilterData = (currentPage = 0) => {
        setPage(currentPage + 1);
    };

    const handelClickEditRule = ({ ruleId }: RuleData) => {
        navigate(`/product/rule-detail/${ruleId}`);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    useEffect(() => {
        setPagesCount(
            data && data?.data.length > 0 ? Number(data?.rowsCount) : 0,
        );
        const tableData: RowDataType[] = data
            ? data.data.map((fullInfo) => {
                  return {
                      ...fullInfo,
                      action: (
                          <Box
                              display={'flex'}
                              justifyContent={'center'}
                              gap={2}
                          >
                              <Tooltip title="Arcive">
                                  <Delete
                                      cursor="pointer"
                                      onClick={() => {
                                          dispatch(
                                              openConfirmationDialogs({
                                                  textInfo:
                                                      'Are You Sure Want To Delete Rule',
                                                  handleConfirm: deleteRule,
                                                  parameters: {
                                                      ruleId: fullInfo.id,
                                                  },
                                              }),
                                          );
                                      }}
                                  />
                              </Tooltip>

                              <Tooltip title="Edit Condition">
                                  <Condition
                                      cursor="pointer"
                                      onClick={() =>
                                          handelClickEditRule({
                                              ruleId: fullInfo.id,
                                          })
                                      }
                                  />
                              </Tooltip>
                          </Box>
                      ),
                      rowComponent: StyledTableRow,
                  };
              })
            : [];
        setTableData(tableData);
    }, [data]);

    useEffect(() => {
        if (dataRuleRequestList) {
            getRuleList({
                ...dataRuleRequestList,
                sort: sort,
                page: page,
                pageSize: pageSize,
            });
        }
    }, [dataRuleRequestList, page, pageSize]);

    useEffect(() => {
        if (deleteIsSuccess) {
            getRuleList(dataRuleRequestList);
        }
    }, [deleteIsSuccess]);

    useEffect(() => {
        dispatch(openBackdrop({ open: isFetching }));
    }, [isFetching]);

    useEffect(() => {
        const error = deleteError;

        if (error && 'data' in error && error.data?.message) {
            dispatch(
                openSnackbar({
                    message: error.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [deleteIsError]);

    return (
        <Box>
            <Filter defaultOpen={false} />

            <Box
                bgcolor={colors.secondary.light}
                borderRadius={borders.borderRadius.md}
                mt={pxToRem(7)}
            >
                <FlexibleTable
                    tableMetaDataList={tableMetaDataList}
                    setTableMetaDataList={setTableMetaDataList}
                    tableDataList={tableData}
                    sort={sort}
                    setSort={setSort}
                    createNewFun={createNewRule}
                    buttonName={'Creat New Rule'}
                />

                {pagesCount > 0 && (
                    <Stack spacing={2} alignItems={'end'}>
                        <TablePagination
                            rowsPerPageOptions={[10, 30, 50]}
                            component="div"
                            count={pagesCount}
                            rowsPerPage={pageSize}
                            page={page - 1}
                            labelRowsPerPage={'Number of rows'}
                            slotProps={{
                                select: {
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    MenuProps: {
                                        disablePortal: true,
                                        slotProps: {
                                            paper: {
                                                sx: {
                                                    backgroundColor:
                                                        colors.secondary.main,
                                                    padding: 0,
                                                },
                                            },
                                        },
                                    },
                                    native: false,
                                },
                            }}
                            onPageChange={(_, page) => handleFilterData(page)}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Stack>
                )}
            </Box>
        </Box>
    );
};

export default Rule;
